/* eslint-disable i18next/no-literal-string */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Typography } from '@mui/material';
import SearchCard from './SearchCard';
import { useState } from 'react';

interface SearchParams {
  email: string;
  uuid: string;
  firstName: string;
  lastName: string;
}

interface StaffSearchFormProps {
  searchParams: SearchParams;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearch: () => void;
  handleClear?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const StaffSearchForm = ({
  searchParams,
  handleInputChange,
  handleSearch,
  handleClear,
  onKeyDown,
}: StaffSearchFormProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const theme = useTheme();
  const hasInput =
    searchParams.email ||
    searchParams.uuid ||
    searchParams.firstName ||
    searchParams.lastName;

  return (
    <>
      <SearchCard data-testid="staff-search-card">
        <Box
          data-testid="staff-search-form"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'flex-end',
            width: '100%',
          }}
        >
          <TextField
            label="UUID or Email"
            variant="outlined"
            name="identifier"
            fullWidth
            value={searchParams.uuid || searchParams.email}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            data-testid="identifier-field"
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: '0' },
            }}
            InputProps={{
              sx: {
                borderRadius: {
                  xs: '10px',
                  md: '25px 0 0 25px',
                },
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                '& fieldset': {
                  borderColor: alpha(theme.palette.divider, 0.2),
                  borderWidth: '1px',
                  borderRightColor: alpha(theme.palette.divider, 0.2),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  borderWidth: '1px',
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '1px',
                  borderRight: { xs: undefined, md: '1px solid' },
                  borderRightColor: alpha(theme.palette.divider, 0.2),
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: alpha(theme.palette.text.primary, 0.6),
              },
            }}
          />

          <TextField
            label="First Name"
            variant="outlined"
            name="firstName"
            fullWidth
            value={searchParams.firstName}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            data-testid="first-name-field"
            sx={{
              flex: 1,
              borderRadius: {
                xs: '10px',
                md: '50px',
              },
              minWidth: { xs: '100%', md: '0' },
              '& .MuiOutlinedInput-root': {
                borderRadius: {
                  xs: '10px',
                  md: '0px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.2),
                borderWidth: '1px',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  borderWidth: '1px',
                },
              '& .MuiOutlinedInput-root.Mui-focused': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '1px',
                },
            }}
            InputProps={{
              sx: {
                borderRadius: 0,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
              },
            }}
            InputLabelProps={{
              sx: {
                color: alpha(theme.palette.text.primary, 0.6),
              },
            }}
          />

          <TextField
            label="Last Name"
            variant="outlined"
            name="lastName"
            fullWidth
            value={searchParams.lastName}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            data-testid="last-name-field"
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: '0' },
            }}
            InputProps={{
              sx: {
                borderRadius: {
                  xs: 3,
                  md: '0 0 0 0',
                },
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                '& fieldset': {
                  borderColor: alpha(theme.palette.divider, 0.2),
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  borderWidth: '1px',
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '1px',
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: alpha(theme.palette.text.primary, 0.6),
              },
            }}
          />

          <Box
            data-testid="search-form-actions"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'row', md: 'row' },
              gap: 2,
              flex: 1,
              mt: { xs: 2, md: 0 },
              alignItems: 'center',
            }}
          >
            {handleClear && hasInput && (
              <Button
                variant="outlined"
                onClick={handleClear}
                startIcon={<ClearIcon />}
                data-testid="clear-button"
                sx={{
                  borderRadius: {
                    xs: '10px',
                    md: '0 0 0 0',
                  },
                  height: '56px',
                  py: 1.7,
                  px: { xs: 2, md: 3 },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  letterSpacing: '0.02em',
                  // minWidth: { xs: '100%', md: '100px' },
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: theme.palette.text.secondary,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.divider, 0.05),
                    borderColor: alpha(theme.palette.divider, 0.5),
                    boxShadow: 'none',
                  },
                }}
              >
                Clear
              </Button>
            )}

            <Button
              variant="contained"
              onClick={() => {
                if (!hasInput) setShowWarning(true);
                else if (hasInput) {
                  handleSearch();
                  setShowWarning(false);
                }
              }}
              startIcon={<SearchIcon />}
              data-testid="search-button"
              sx={{
                borderRadius: {
                  xs: 3,
                  md:
                    hasInput && handleClear ? '0 25px 25px 0' : '0 25px 25px 0',
                },
                height: '56px',
                py: 1.7,
                px: 4,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.8,
                )} 100%)`,
                flex: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16)',
                },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </SearchCard>
      {showWarning && (
        <Typography
          data-testid="search-warning-message"
          sx={{ textAlign: 'center', marginTop: '20px', color: 'red' }}
        >
          Please enter at least one parameter.
        </Typography>
      )}
    </>
  );
};

export default StaffSearchForm;
